import { useState, useCallback, useRef } from 'react'
import type { BdrLead } from '@/types'
import { emptyNewLead, emptyRegTentativa } from '@/utils/bdrPanelUtils'

type NewLeadForm = ReturnType<typeof emptyNewLead>
type RegTentativaForm = ReturnType<typeof emptyRegTentativa>

export function useBdrPanelModalState() {
  const [showModal, setShowModal] = useState(false)
  const [selectedLead, setSelectedLead] = useState<BdrLead | null>(null)
  const [isNewLead, setIsNewLead] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showRegTentativa, setShowRegTentativa] = useState(false)
  const [savingTentativa, setSavingTentativa] = useState(false)
  const [backendErrors, setBackendErrors] = useState<Record<string, string>>({})
  const [newLead, setNewLead] = useState<NewLeadForm>(emptyNewLead())
  const [regTentativa, setRegTentativa] = useState<RegTentativaForm>(emptyRegTentativa())

  const formRef = useRef<HTMLFormElement | null>(null)

  const handleCloseModal = useCallback(() => {
    setShowModal(false)
    setSelectedLead(null)
    setIsNewLead(false)
    setIsEditing(false)
    setShowRegTentativa(false)
    setSavingTentativa(false)
    setBackendErrors({})
    setNewLead(emptyNewLead())
    setRegTentativa(emptyRegTentativa())
  }, [])

  const handleOpenNewLead = useCallback(() => {
    setNewLead(emptyNewLead())
    setIsNewLead(true)
    setIsEditing(false)
    setSelectedLead(null)
    setBackendErrors({})
    setShowRegTentativa(false)
    setShowModal(true)
  }, [])

  const handleOpenLeadDetail = useCallback((lead: BdrLead, openTentativa = false, initialChannel?: string) => {
    setSelectedLead(lead)
    setIsNewLead(false)
    setIsEditing(false)
    setBackendErrors({})

    const initialTentativa = emptyRegTentativa()
    initialTentativa.email = lead.email ?? ''
    if (initialChannel) {
      initialTentativa.canal = initialChannel
    }

    setRegTentativa(initialTentativa)
    setShowRegTentativa(openTentativa || !!initialChannel)
    setShowModal(true)

    if ((initialChannel === 'WhatsApp' || initialChannel === 'Breakup') && lead.telefone) {
      const cleanPhone = lead.telefone.replace(/\D/g, '')
      const finalPhone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`
      const wppWindow = window.open(`https://api.whatsapp.com/send?phone=${finalPhone}`, '_blank')

      if (wppWindow) {
        setTimeout(() => {
          try {
            wppWindow.close()
          } catch {
            console.warn('Wpp auto-close blocked')
          }
        }, 1500)
      }
    }
  }, [])

  const handleStartEdit = useCallback((lead: BdrLead) => {
    setNewLead({
      empresa: lead.empresa ?? '',
      contato: lead.contato ?? '',
      cargo: lead.cargo ?? '',
      telefone: lead.telefone ?? '',
      email: lead.email ?? '',
      site: lead.site ?? '',
      segmento: lead.segmento ?? '',
      plataforma: lead.plataforma ?? '',
      modelo: lead.modelo ?? '',
      faturamento: lead.faturamento ?? '',
      origem: lead.origem ?? '',
      problema: lead.problema ?? '',
      observacoes: lead.observacoes ?? '',
      servicos: lead.servicos ?? [],
      trafego: lead.trafego ?? '',
      cnpj: lead.cnpj ?? '',
      parceiroId: lead.parceiroId ?? '',
      empresaId: lead.empresaId ?? '',
    })
    setIsEditing(true)
    setIsNewLead(false)
    setBackendErrors({})
  }, [])

  return {
    formRef,
    showModal,
    setShowModal,
    handleCloseModal,
    handleOpenNewLead,
    handleOpenLeadDetail,
    selectedLead,
    setSelectedLead,
    isNewLead,
    setIsNewLead,
    isEditing,
    setIsEditing,
    showRegTentativa,
    setShowRegTentativa,
    savingTentativa,
    setSavingTentativa,
    backendErrors,
    setBackendErrors,
    newLead,
    setNewLead,
    regTentativa,
    setRegTentativa,
    handleStartEdit,
  }
}
